import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  NgZone,
  OnInit,
  signal,
  viewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  FormControl
} from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { AsYouType, CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';

import clone from 'lodash-es/clone';
import compact from 'lodash-es/compact';
import filter from 'lodash-es/filter';
import find from 'lodash-es/find';
import map from 'lodash-es/map';

import CONSTANTS from '@constants';
import dayjs from 'dayjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { featureFlags } from '@utils/feature-flags';

type UiCountry = {
  code: string;
  name: string;
  phoneCode: string;
};

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'intl-phone' }
})
export class PhoneInputComponent implements OnInit, ControlValueAccessor, Validator {
  phoneInput = viewChild<ElementRef<HTMLInputElement>>('phoneInput');
  isNewDesignOn = signal(featureFlags.isOn('Frontend.jobShareListRedesign'));

  country: UiCountry = null;
  highlightedCountriesCodes = ['AT', 'DE', 'CH', 'HU', 'NL'];
  isDropdownOpened = false;
  phoneControl = new FormControl();
  searchControl = new FormControl();
  searchValue: string = '';

  // Unfiltered list of all countries
  allCountries: UiCountry[] = compact(
    map(CONSTANTS.COUNTRIES, (name, code) => {
      try {
        return { code, name, phoneCode: getCountryCallingCode(code as CountryCode) as string };
      } catch (e) {
        return null;
      }
    })
  );

  // Unfiltered list of all the countries that should stay on top of the list
  allHighlightedCountries: UiCountry[] = filter(this.allCountries, country => {
    return this.highlightedCountriesCodes.indexOf(country.code) !== -1;
  });

  // Unfiltered list of all other not highlighted countries
  allNotHighlightedCountries: UiCountry[] = filter(this.allCountries, country => {
    return this.highlightedCountriesCodes.indexOf(country.code) === -1;
  });

  // Filtered lists, which used for the UI output
  highlightedCountries = signal<UiCountry[]>(clone(this.allHighlightedCountries));
  notHighlightedCountries = signal<UiCountry[]>(clone(this.allNotHighlightedCountries));

  onChanged: (val: string) => void;
  onTouched: () => void;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone
  ) {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(value => {
        this.searchValue = value;
      });

    this.phoneControl.valueChanges
      .pipe(debounceTime(300), skip(1), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(value => this.handlePhoneInput(value));
  }

  ngOnInit() {
    const detectedCountry = this.findTimezoneCountry(dayjs.tz.guess());
    const foundCountry = this.getCountryByCode(detectedCountry || 'DE');
    this.selectCountry(null, foundCountry);
  }

  handlePhoneInput(input: string) {
    const formatter = new AsYouType();
    formatter.input(input || '');
    if (this.country?.code !== formatter.country) {
      this.country = this.getCountryByCode(formatter.country);
    }

    const valid = input ? formatter.isValid() : true;
    this.phoneControl.setErrors(valid ? null : { phoneNumber: true });
    this.onTouched();
    this.phoneControl.markAsDirty();
    this.onChanged(valid ? input : '');
  }

  writeValue(value: string): void {
    if (value) {
      this.phoneControl.setValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate({ value }: AbstractControl): ValidationErrors {
    return this.phoneControl.errors;
  }

  dropdownOpened() {
    this.isDropdownOpened = true;

    this.zone.runOutsideAngular(() => {
      this.document.querySelector<HTMLInputElement>('#phoneInputSearch').focus();
    });
  }

  dropdownClosed() {
    this.isDropdownOpened = false;
  }

  getCountryByCode(code) {
    return find(this.allCountries, { code });
  }

  /**
   * Sets country, then updates phone number to use new country phone code,
   * after announces update to listeners, sets new placeholder for the phone input,
   * and finally returns focus to phone input.
   * @param event {Event} UI event, if it's falsy, code assumes that function was called programmatically
   * @param country {UiCountry}
   */
  selectCountry(event: Event, country: UiCountry) {
    this.country = country;
    this.phoneControl.setValue(`+${country.phoneCode}`);

    if (event) {
      event.preventDefault();
      this.phoneInput().nativeElement.focus();
    }
  }

  // Finds Timezone by time zone name
  findTimezoneCountry(zoneName: string) {
    const foundTimezone = find(CONSTANTS.TIMEZONES, { zoneName });
    return foundTimezone ? foundTimezone.countryCode : null;
  }
}
